// De statuswoorden van een CV Send en van een interviewronde (eigenaarsbesluit
// 20 september 2026): EEN woordenschat, op elk scherm hetzelfde. Pure functies,
// geen React, zodat drie consumenten uit dezelfde bron lezen:
//   components/HomeV2/lib.js exporteert ze door voor de componenten,
//   lib/processen-rows.mjs krijgt ze als labelfunctie binnen (die module blijft
//   vrij van component-imports),
//   lib/cv-send-row-actions.test.mjs test ze hier rechtstreeks.
//
// Alles hieronder is WEERGAVE. De opgeslagen waarde blijft de ruwe status van
// het externe systeem; niets hier schrijft ooit terug.

import { processReached, rejectedStage } from './process-reached.mjs';

// De ruwe importstatussen, kleingeletterd als sleutel. De app noemt een
// verstuurd CV een 'CV Send' en een afwijzing draagt wie afwees, want 'Rejected'
// en "Didn't go for it" zijn twee verschillende gebeurtenissen.
export const RCRM_STATUS_LABELS = {
  'cv sent': 'CV Send',
  'assigned to job': 'CV Send',
  assigned: 'CV Send',
  '1st iv': 'Interview 1',
  '2nd iv': 'Interview 2',
  '3rd iv': 'Interview 3',
  offer: 'Aanbieding',
  wfb: 'Wacht op start',
  contract: 'Plaatsing',
  placed: 'Plaatsing',
  won: 'Plaatsing',
  'did not join': 'Afgewezen (door kandidaat)',
  "didn't go for it": 'Afgewezen (door kandidaat)',
  rejected: 'Afgewezen (door klant)',
  'call scheduled': 'Gesprek gepland',
  applied: 'Gesolliciteerd',
  'on hold': 'Gepauzeerd',
};

// De import levert de apostrof nu eens recht en dan weer krom; dat is dezelfde
// status, dus normaliseer hem weg voordat de sleutel wordt opgezocht.
const rcrmKey = (s) => String(s ?? '').replace(/’/g, "'").trim().toLowerCase();

// Het volle woord: de titel van een rij, de hover van een pil.
export function rcrmStatusLabel(s) {
  const raw = String(s ?? '');
  return RCRM_STATUS_LABELS[rcrmKey(raw)] || raw;
}

// De korte vorm voor de vaste StatusPill van 128px (RecordGrid.js): alleen de
// twee afwijzingen zijn daar te lang, de rest is al kort genoeg.
const RCRM_STATUS_SHORT = {
  'Afgewezen (door kandidaat)': 'Afgew. kandidaat',
  'Afgewezen (door klant)': 'Afgew. klant',
};

export function rcrmStatusShort(s) {
  const label = rcrmStatusLabel(s);
  return RCRM_STATUS_SHORT[label] || label;
}

// Een afwijzing leest rood, de rest neutraal. De toonkeuze hoort bij de
// woordenschat en niet bij de rekenmodule, anders staat de lijst met
// afwijzingswoorden op twee plekken.
export function rcrmStatusTone(s) {
  return Object.prototype.hasOwnProperty.call(RCRM_STATUS_SHORT, rcrmStatusLabel(s)) ? 'red' : 'neutral';
}

// De drie procesfasen die in de app een langere naam dragen dan in de database.
export function statusShort(s) {
  if (s === 'Aanbieding arbeidscontract') return 'Aanbieding';
  if (s === 'Vragen over Raamovereenkomst') return 'Onderhandeling';
  if (s === 'Signed Deal (Getekend)') return 'Getekend';
  return s;
}

// Het statuswoord van een PROCES (eigenaarsbesluit 26 september 2026): een getekend
// proces heet 'Getekend', op elk scherm. De opgeslagen status van een getekend proces
// heeft vier schrijvers en daardoor zes waarden ('Plaatsing', 'Signed Deal (Getekend)',
// 'New Signed Client NCNP', 'New Signed Client Retained', of een IV- of aanbiedingsfase
// die bleef staan). Die blijven in de database staan; dit is de lezing. De kolom en het
// filter Status van het Processen-raster lezen processStatusSql, een scherm met een
// deal in handen leest processStatus. De mijlpaal 'Geplaatst' van een kandidaat is een
// ander feit (lib/crm/candidate-milestones.mjs) en blijft zo heten.
export function processStatus(deal) {
  if (!deal) return '';
  return deal.signed ? 'Getekend' : String(deal.status || '');
}

// De SQL-tweeling van processStatus op de deal-alias `alias` (een vertrouwde
// codeconstante, nooit invoer).
export const processStatusSql = (alias) => `(CASE WHEN COALESCE((${alias}.data->>'signed')::boolean, false) THEN 'Getekend' ELSE ${alias}.data->>'status' END)`;

// Rondelabel van een interviewproces: 'IV1' is een databasewaarde, 'Interview 1'
// is wat op het scherm hoort te staan. Zowel de chip op een CV Send-rij als de
// fasekolom van het Processen-raster leest dit.
export function intDealRoundLabel(intDeal) {
  if (!intDeal) return 'INT Proces';
  if (intDeal.signed) return 'Getekend';
  // Een afgewezen proces zegt waar het afliep, niet de ronde waar het bleef staan.
  if (intDeal.rejected) return rejectedStage(processReached(intDeal)).label;
  const m = /^IV(\d)$/.exec(String(intDeal.status || ''));
  if (m) return `Interview ${m[1]}`;
  return statusShort(intDeal.status || '') || 'Interview 1';
}
